import { KeyValuePipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DropdownModule } from 'primeng/dropdown';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { AppIcons } from '../../../../shared/app.icons';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../../shared/services/entity.service';
import type { AttributeData, AttributeDataTab, FluffyGroup } from '../../../../shared/models/attribute.models';

interface DropdownItem {
  name: string;
  code: string;
}

interface SponsorDetails {
  entityId: string;
  attributeCodeValueDtoList: Array<{
    attributeCode: string;
    attributeValue: string;
  }>;
  [key: string]: unknown;
}

interface VehicleRowData {
  [key: string]: unknown;
}

interface DriverList {
  [key: string]: unknown;
}

@Component({
  selector: 'app-sponsor-overview-details',
  standalone: true,
  imports: [ButtonModule, TranslateModule, DropdownModule, ChartModule, OverlayPanelModule, FormsModule,
    NgClass, KeyValuePipe],
  templateUrl: './overview-details.component.html',
  styleUrl: './overview-details.component.scss'
})
export class SponsorOverviewDetailsComponent implements OnInit, OnChanges {
  miIcons = AppIcons;
  iconPos: 'left' | 'right' = 'right';
  @Input() attributeValues: Record<string, unknown>;
  @Input() attributeLabels: Record<string, string>;
  @Input() sponsorAttributeData: AttributeData;
  @Input() vehicleEntityId: string;
  @Input() vehicleRowData: VehicleRowData;
  @Input() sponsorDetails: SponsorDetails;
  @Input() driverList: DriverList[];
  @Input() isDeleted: boolean = false;
  @Input() isUnassigned: boolean = false;
  @Output() onToggleOverLay: EventEmitter<boolean> = new EventEmitter();

  dropDownItems: DropdownItem[] = [];
  selectedItem: DropdownItem[] = [];
  data: unknown;
  options: unknown;
  tab: AttributeDataTab;
  details: FluffyGroup;
  center: google.maps.LatLngLiteral = {
    lat: 24,
    lng: 12,
  };
  zoom = 4;
  map: google.maps.Map;
  bulkRowCount = 1;
  rowData: VehicleRowData;
  isLoading: boolean = false;
  selectedEventId: string;
  eventLat: number;
  eventLng: number;
  marker: google.maps.Marker;
  zoneIds: string[] = [];
  fields: Array<{ attributeCode: string;[key: string]: unknown }>;
  matchedZoneDisplayName: string = '';
  sponsorStatus: string;
  sponsorEmail: string;
  sponsorCode: string;
  sponsorName: string;
  sponsorAddress: string;
  sponsorPhone: string;
  billingEntity: string;
  abnNumber: string;
  accountNumber: string;
  accContactName: string;
  accContactEmail: string;
  myObCustomerId: string;

  constructor(public sponsorService: EntityService,
    public cs: CommonBindingDataService, private router: Router,
    private cd: ChangeDetectorRef,
  ) { }

  ngOnChanges() {
    this.rowData = this.vehicleRowData;
  }

  ngOnInit() {
    if (this.sponsorDetails?.attributeCodeValueDtoList) {
      this.loadOverviewDetails()
    }
    if (this.sponsorAttributeData && this.sponsorAttributeData.tabs) {
      this.tab = this.sponsorAttributeData.tabs.find(ele => ele.tabCode === 'overview');
    }
    if (this.tab && this.tab.groups) {
      this.details = this.tab.groups.find(ele => ele.code === 'details');
    }
  }

  loadOverviewDetails() {
    this.sponsorStatus = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'sponsor_status');
    this.sponsorCode = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'sponsor_code');
    this.sponsorName = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'sponsor_name');
    this.myObCustomerId = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'myob_customer_id');
    this.sponsorAddress = this.getFullAddress();
    this.sponsorPhone = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'contact_number');
    this.sponsorEmail = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'email');
    this.billingEntity = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'billing_entity');
    this.abnNumber = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'abn_number');
    this.accountNumber = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'account_number');
    this.accContactName = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'acc_contact_name');
    this.accContactEmail = this.getAttributeValue(this.sponsorDetails?.attributeCodeValueDtoList, 'acc_contact_email');
  }

  getAttributeValue(valueList, attributeCode) {
    return valueList.filter(ele => ele.attributeCode === attributeCode)[0]?.attributeValue;
  }

  getFullAddress() {
    const addressParts = [
      this.getAddressEntity(this.sponsorDetails?.attributeCodeValueDtoList, 'spn_addr_name'),
      this.getAddressEntity(this.sponsorDetails?.attributeCodeValueDtoList, 'spn_addr_line_1'),
      this.getAddressEntity(this.sponsorDetails?.attributeCodeValueDtoList, 'spn_addr_line_2'),
      this.getAddressEntity(this.sponsorDetails?.attributeCodeValueDtoList, 'spn_street'),
      this.getAddressEntity(this.sponsorDetails?.attributeCodeValueDtoList, 'spn_city'),
      this.getAddressEntity(this.sponsorDetails?.attributeCodeValueDtoList, 'spn_state'),
      this.getAddressEntity(this.sponsorDetails?.attributeCodeValueDtoList, 'spn_zip')
    ];
    return addressParts.filter(Boolean).join(', ');
  }

  getAddressEntity(valueList, attributeCode) {
    return valueList.filter(ele => ele.attributeCode === attributeCode)[0]?.attributeValue || '';
  }

  redirectToEdit() {
    this.router.navigate(['/app/sponsors/add-sponsor'], { queryParams: { id: this.sponsorDetails.entityId } });
  }
}
